<template>
    <div class="page-container" :class="theme">
        <canvas class="particles"></canvas>
        <div class="theme-toggle" @click="toggleTheme">
            {{ theme === 'dark' ? '☀️' : '🌙' }}
        </div>
        <div class="back-button" @click="$router.push('/')">← Go Back</div>
        <div class="progress-bar" :style="{ width: scrollProgress + '%' }"></div>
        
        <div class="main-container">
            <div class="header-section">
                <h1 class="page-title"> Thoughts on my Relevant Classes</h1>
                <p class="subtitle">Reflections on my academic journey through Computer Science and Linguistics</p>
            </div>

            <div class="classes-grid">
                <div class="class-card" 
                     v-for="(classItem, index) in classes" 
                     :key="index"
                     :style="{ animationDelay: index * 0.1 + 's' }"
                     @click="toggleClass(index)">
                    <div class="card-header">
                        <div class="class-icon">{{ classItem.icon }}</div>
                        <h2 class="class-title">{{ classItem.name }}</h2>
                        <div class="expand-icon" :class="{ 'rotated': activeIndex === index }">
                            {{ activeIndex === index ? '▲' : '▼' }}
                        </div>
                    </div>
                    
                    <transition
                        name="slide-fade"
                        @enter="startTransition"
                        @leave="endTransition"
                    >
                        <div v-show="activeIndex === index" 
                             :class="['class-content', { 'visible': activeIndex === index }]">
                            <div class="thoughts-section">
                                <h3>My Thoughts & Experience</h3>
                                <div v-if="Array.isArray(classItem.thoughts)">
                                    <p v-for="(paragraph, pIndex) in classItem.thoughts" 
                                       :key="pIndex" 
                                       class="thoughts-text">{{ paragraph }}</p>
                                </div>
                                <p v-else class="thoughts-text">{{ classItem.thoughts }}</p>
                                
                                <div class="key-topics" v-if="classItem.keyTopics.length">
                                    <h4>Key Topics Covered</h4>
                                    <div class="topics-tags">
                                        <span v-for="(topic, topicIndex) in classItem.keyTopics" 
                                              :key="topicIndex" 
                                              class="topic-tag">
                                            {{ topic }}
                                        </span>
                                    </div>
                                </div>

                                <div class="difficulty-rating">
                                    <h4>Difficulty Rating</h4>
                                    <div class="rating-stars">
                                        <span v-for="star in 5" 
                                              :key="star" 
                                              class="star"
                                              :class="{ 'filled': star <= classItem.difficulty }">
                                            ⭐
                                        </span>
                                        <span class="rating-text">({{ classItem.difficulty }}/5)</span>
                                    </div>
                                </div>

                                <div class="enjoyment-rating">
                                    <h4>Enjoyment Level</h4>
                                    <div class="enjoyment-bar">
                                        <div class="enjoyment-fill" :style="{ width: classItem.enjoyment + '%' }"></div>
                                        <span class="enjoyment-text">{{ classItem.enjoyment }}%</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            theme: 'dark',
            activeIndex: null,
            scrollProgress: 0,
            classes: [
                {
                    name: "Data Structures and Algorithms",
                    // icon: "",
                    thoughts: [
                        "This was one of the most fundamental and important classes (taught by Prof Nachenberg for Data Structures and Prof Korb for Algorithms) I've taken. However, I struggled in these classes and writing a lot of proofs was a lot of work.",
                        "I get to learn efficient problem-solving and algorithmic thinking. The way we learned to analyze time and space complexity changed how I approach coding problems entirely.",
                        "Working with trees, graphs, and dynamic programming was challenging but incredibly rewarding."
                    ],
                    keyTopics: ["Binary Trees", "Graph Algorithms", "Dynamic Programming", "Sorting Algorithms", "Hash Tables", "Big O Analysis", "Recursion", "Greedy Algorithms", "Algorithm Design"],
                    difficulty: 4,
                    enjoyment: 85
                },
                {
                    name: "Introduction to Data Science",
                    // icon: "",
                    thoughts: [
                        "This class (taught by Prof Dolecek) introduced me to the practical side of data analysis and machine learning. I loved working with real datasets and learning how to extract meaningful insights from raw data.",
                        "The homeworks and exam for this class is quite tough as it requires a lot of multivariable and linear algebra concepts, but that is expected of a data science course.",
                        "It definitely sparked my interest in ML and AI applications. and I would definitely recommend it to anyone interested in data science!"
                    ],
                    keyTopics: ["Pandas", "NumPy", "Machine Learning", "Data Visualization", "Statistical Analysis", "Feature Engineering", "Data Cleaning", "Exploratory Data Analysis", "Model Evaluation"],
                    difficulty: 2,
                    enjoyment: 95
                },
                {
                    name: "Deep Learning in Computer Vision",
                    // icon: "",
                    thoughts: [
                        "Absolutely mind-blowing class ! Learning how neural networks can 'see' and interpret images was incredible.",
                        "The progression from basic CNNs to advanced architectures like ResNet and transformers was fascinating. Also learning about how GANs and VAEs work were so interesting to me!",
                        "Working on image classification and object detection projects were also cool.",
                        "But, beware, this class (taught by Prof. Bolei), is not for the faint of heart. The final exam is 50% worth of your grade and it can be very tough!",
                        "So, Study for it and this class is definitely worth it!"
                    ],
                    keyTopics: ["Convolutional Neural Networks", "Image Classification", "Object Detection", "Transfer Learning", "PyTorch", "Computer Vision"],
                    difficulty: 4,
                    enjoyment: 95
                },
                {
                    name: "Advanced Neural Networks and Deep Learning (Graduate)",
                    // icon: "",
                    thoughts: [
                        "This is the 2nd version of the Deep Learning in Computer Vision Class (taught by Prof Kao), but at a graduate level.",
                        "The pace of this class is as follows: GANs and VAEs, Conditional DDPM, basic Transformer GPT and BERT, and lastly, Reinforcement Learning (DQN).",
                        "I think this is easier than the undergraduate version because it is project-based where each of your project is worth 25% of your grade.",
                        "So, I think this class is a great way to deepen your understanding of neural networks and deep learning, especially if you're interested in research or advanced applications."
                    ],
                    keyTopics: ["GANs", "VAEs", "Conditional DDPM", "Transformer Models", "GPT", "BERT", "Reinforcement Learning", "DQN", "Research Papers"],
                    difficulty: 3,
                    enjoyment: 100
                },
                {
                    name: "Computational Linguistics I",
                    // icon: "",
                    thoughts: [
                        "This class was taught in the Linguistics Department and it was taught by Prof Laurel. This class can be a bit tough, but it was a great way to bridge my two interests in CS and Linguistics.",
                        "Also, this class is in Haskell and I didn't take CS 181 (formal automata theory) beforehand but it was very doable",
                        "The linguistics concepts, combined with some computer science concepts, such as formal grammars, automata theory, and parsing algorithms (top-down, bottom-up), made this class a unique experience.",
                        "Chomsky's theories on syntax and semantics were fascinating, and applying them computationally was a great challenge.",    
                        "This class is very fun, but I would not recommend doing the homeworks and the project on the day off. That was a mistake of mine but I still got a good grade."
                    ],
                    keyTopics: ["Natural Language Processing", "Syntax Parsing", "Semantic Analysis", "Language Models", "Linguistic Theory", "Text Processing", "Haskell"],
                    difficulty: 4,
                    enjoyment: 92
                },
                {
                    name: "Compiler Construction",
                    // icon: "",
                    thoughts: [
                        "One of the most challenging but rewarding classes I've taken. Building a compiler from scratch really deepened my understanding of how programming languages work under the hood.",
                        "The Compiler Project was very very difficult because I haven't coded in Java since AP Computer Science A (4 years ago) and I had to learn Java from scratch again",
                        "The Project started from writing miniJava (type checking), then implementing IR, Sparrow, (Intermediate Representation), which is translating from minijava to sparrow",
                        "Then, lastly, we implemented optimization (SparrowV) and code generation (RiscV), so it was a comprehensive project that covered many aspects of compiler design.",
                        "Although this class can be very rewarding, it will take ALOT of work (roughly 1000+ lines of code per homework) and time to complete the project, so be prepared for that. (this class is taught by Prof Palsberg)",
                    ],
                    keyTopics: ["Lexical Analysis", "Parsing", "Code Generation", "Optimization", "Symbol Tables", "Assembly Language", "Intermediate Representation", "Java", "Compiler Design"],
                    difficulty: 5,
                    enjoyment: 80
                },
                {
                    name: "Phonology I",
                    // icon: "",
                    thoughts: [
                        "Taught by Prof Kie, this class was very cool",
                        "Understanding phonological rules and patterns was like discovering hidden structures in human speech.",
                        "I really like how Prof Kie teaches the rules and notations needed for homeworks and exams and it was very helpful.",
                        "Would recommend 10/10 if you are interested in Linguistics and Phonology.",
                    ],
                    keyTopics: ["Phonological Rules", "Sound Systems", "Feature Geometry", "Syllable Structure", "Phonological Processes", "Language Variation", "Morphology", "Give Derivations"],
                    difficulty: 3,
                    enjoyment: 90
                },
                {
                    name: "Morphology",
                    // icon: "",
                    thoughts: [
                        "Taught by Prof Silvestri and this class was very fun and engaging.",
                        "There was no exams, just 2 quizzes and a research paper broken into 3 parts and she guides you through it to make sure you understand everything",
                        "I really like how every lecture, she explains morphological concepts and theories in a clear and concise way.",
                        "Would recommend 10/10 if you are interested in Morphology. And besides Prof Silvestri is very nice and approachable.",
                   ],
                    keyTopics: ["Word Formation", "Morphological Analysis", "Inflection and Derivation", "Morphological Typology", "Morphosyntax", "Lexical Semantics", "Morphological Rules", "Language Comparison", "Research Paper and Methods"],
                    difficulty: 2,
                    enjoyment: 100
                },
                {
                    name: "Syntax II",
                    // icon: "",
                    thoughts: [
                        "Taught by Prof Silvestri and this class is the 2nd version of Syntax I (Ling 120B).",
                        "There was 1 midterm, 1 final, and there was 6 homework assignments. These were very doable and the midterm and final were very fair.",
                        "I really like how every lecture, she explains Syntax II concepts and theories in a clear and concise way.",
                        "My favorite part of this course is probably how syntax is not just about rules, but also about understanding how language works at a deeper level.",
                        "Would recommend 10/10 if you are interested in Syntax II. And besides Prof Silvestri is very nice and approachable.",
                   ],
                    keyTopics: ["Phrase Structure", "X-Bar Theory II", "Movement", "Binding Theory", "Case Theory", "Syntactic Structures", "Cross-Linguistic Syntax", "Syntax and Semantics", "Syntax Trees"],
                    difficulty: 3,
                    enjoyment: 90
                }
            ]
        }
    },
    mounted() {
        this.initParticles();
        window.addEventListener('scroll', this.updateScrollProgress);
        this.theme = localStorage.getItem('theme') || 'dark';
    },
    beforeUnmount() {
        window.removeEventListener('scroll', this.updateScrollProgress);
    },
    methods: {
        toggleClass(index) {
            this.activeIndex = this.activeIndex === index ? null : index;
        },
        startTransition(el) {
            const height = el.scrollHeight;
            el.style.height = '0';
            el.offsetHeight; // force reflow
            el.style.height = height + 'px';
        },
        endTransition(el) {
            el.style.height = el.scrollHeight + 'px';
            el.offsetHeight; // force reflow
            el.style.height = '0';
        },
        initParticles() {
            const canvas = document.querySelector('.particles');
            if (!canvas) return;
            
            const ctx = canvas.getContext('2d');
            const particles = [];
            
            const resizeCanvas = () => {
                canvas.width = window.innerWidth;
                canvas.height = window.innerHeight;
            };
            
            window.addEventListener('resize', resizeCanvas);
            resizeCanvas();

            class Particle {
                constructor() {
                    this.x = Math.random() * canvas.width;
                    this.y = Math.random() * canvas.height;
                    this.size = Math.random() * 3 + 1;
                    this.speedX = Math.random() * 3 - 1.5;
                    this.speedY = Math.random() * 3 - 1.5;
                    this.opacity = Math.random() * 0.5 + 0.3;
                }

                update() {
                    this.x += this.speedX;
                    this.y += this.speedY;

                    if (this.x > canvas.width) this.x = 0;
                    if (this.x < 0) this.x = canvas.width;
                    if (this.y > canvas.height) this.y = 0;
                    if (this.y < 0) this.y = canvas.height;
                }

                draw() {
                    ctx.fillStyle = `rgba(52, 152, 219, ${this.opacity})`;
                    ctx.beginPath();
                    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
                    ctx.fill();
                }
            }

            const createParticles = () => {
                for (let i = 0; i < 50; i++) {
                    particles.push(new Particle());
                }
            };

            const animate = () => {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                particles.forEach(particle => {
                    particle.update();
                    particle.draw();
                });
                requestAnimationFrame(animate);
            };

            createParticles();
            animate();
        },
        updateScrollProgress() {
            const scrollTop = window.scrollY;
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            this.scrollProgress = (scrollTop / docHeight) * 100;
        },
        toggleTheme() {
            this.theme = this.theme === 'dark' ? 'light' : 'dark';
            localStorage.setItem('theme', this.theme);
        }
    }
}
</script>

<style scoped>
.page-container {
    min-height: 100vh;
    position: relative;
    transition: all 0.3s ease;
    padding: 2rem 1rem;
}

.page-container.dark {
    background-color: #163f4b;
    color: white;
}

.page-container.light {
    background-color: #e0e8f1;
    color: #333;
}

.theme-toggle {
    position: fixed;
    top: 20px;
    right: 20px;
    padding: 10px;
    cursor: pointer;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    transition: all 0.3s ease;
    z-index: 100;
}

.theme-toggle:hover {
    transform: scale(1.1);
}

.back-button {
    position: fixed;
    top: 20px;
    left: 20px;
    padding: 0.75rem 1.5rem;
    background-color: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border-radius: 8px;
    color: rgb(146, 169, 203);
    cursor: pointer;
    z-index: 100;
    transition: all 0.3s ease;
}

.back-button:hover {
    background-color: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
}

.progress-bar {
    position: fixed;
    top: 0;
    left: 0;
    height: 5px;
    background-color: #2980b9;
    z-index: 101;
    transition: width 0.25s ease;
}

.particles {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 0;
}

.main-container {
    max-width: 1000px;
    margin: 80px auto 0;
    position: relative;
    z-index: 10;
}

.header-section {
    text-align: center;
    margin-bottom: 3rem;
    background: rgba(255, 255, 255, 0.1);
    padding: 2rem;
    border-radius: 20px;
    backdrop-filter: blur(10px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.page-title {
    font-size: 3rem;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #3498db, #2980b9);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.subtitle {
    font-size: 1.2rem;
    opacity: 0.8;
    margin: 0;
}

.classes-grid {
    display: grid;
    gap: 1.5rem;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
}

.class-card {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 15px;
    padding: 1.5rem;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    cursor: pointer;
    transition: all 0.3s ease;
    opacity: 0;
    transform: translateY(20px);
    animation: fadeInUp 0.6s ease forwards;
}

.class-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
    background: rgba(255, 255, 255, 0.15);
}

.card-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
}

.class-icon {
    font-size: 2rem;
    min-width: 50px;
    text-align: center;
}

.class-title {
    flex: 1;
    font-size: 1.3rem;
    margin: 0;
    color: #3498db;
}

.expand-icon {
    font-size: 1.2rem;
    transition: transform 0.3s ease;
}

.expand-icon.rotated {
    transform: rotate(180deg);
}

.class-content {
    overflow: hidden;
    transition: height 0.4s ease-out;
    height: 0;
}

.class-content.visible {
    height: auto;
}

.thoughts-section {
    padding-top: 1rem;
}

.thoughts-section h3 {
    color: #2980b9;
    margin-bottom: 1rem;
    font-size: 1.2rem;
}

.thoughts-text {
    line-height: 1.6;
    margin-bottom: 1.5rem;
    text-align: justify;
}

.key-topics {
    margin-bottom: 1.5rem;
}

.key-topics h4 {
    color: #3498db;
    margin-bottom: 0.5rem;
    font-size: 1rem;
}

.topics-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.topic-tag {
    background: rgba(52, 152, 219, 0.2);
    padding: 4px 8px;
    border-radius: 15px;
    font-size: 0.8rem;
    color: #3498db;
    border: 1px solid rgba(52, 152, 219, 0.3);
}

.difficulty-rating, .enjoyment-rating {
    margin-bottom: 1rem;
}

.difficulty-rating h4, .enjoyment-rating h4 {
    color: #3498db;
    margin-bottom: 0.5rem;
    font-size: 1rem;
}

.rating-stars {
    display: flex;
    align-items: center;
    gap: 0.2rem;
}

.star {
    font-size: 1.2rem;
    opacity: 0.3;
    transition: opacity 0.3s ease;
}

.star.filled {
    opacity: 1;
}

.rating-text {
    margin-left: 0.5rem;
    font-size: 0.9rem;
    opacity: 0.7;
}

.enjoyment-bar {
    position: relative;
    background: rgba(255, 255, 255, 0.1);
    height: 20px;
    border-radius: 10px;
    overflow: hidden;
}

.enjoyment-fill {
    height: 100%;
    background: linear-gradient(90deg, #3498db, #2980b9);
    border-radius: 10px;
    transition: width 1s ease;
}

.enjoyment-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 0.8rem;
    font-weight: bold;
    color: white;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.7);
}

/* Light mode styles */
.light .class-title {
    color: #2980b9;
}

.light .thoughts-section h3,
.light .difficulty-rating h4,
.light .enjoyment-rating h4,
.light .key-topics h4 {
    color: #1f5582;
}

.light .topic-tag {
    background: rgba(41, 128, 185, 0.1);
    color: #2980b9;
    border-color: rgba(41, 128, 185, 0.2);
}

.light .class-card {
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(0, 0, 0, 0.1);
}

.light .class-card:hover {
    background: rgba(255, 255, 255, 0.9);
}

/* Animations */
@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.4s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
    height: 0;
    opacity: 0;
}

/* Responsive design */
@media (max-width: 768px) {
    .classes-grid {
        grid-template-columns: 1fr;
    }
    
    .page-title {
        font-size: 2rem;
    }
    
    .class-card {
        padding: 1rem;
    }
    
    .main-container {
        margin-top: 120px;
        padding: 0 1rem;
    }
}
</style>
